/**
 * Integração Convenia (E10a+E10b+E10c — integracao-convenia §4.1/§4.3/§4.5):
 * cartão de status do espelho, cadastro do token (o valor nunca sai pela API),
 * "Atualizar tudo" e sync parcial (domínios × pessoas), histórico de syncs com
 * detalhe expansível (contagens/alertas por domínio + de-para) e a fila de
 * propostas do staging E10c (matrícula, salarial e dependentes): diff
 * antes→depois, alertas (bloqueante = decisão individual), aplicação individual
 * (com vigenciaInicio quando exigida) ou em lote (o lote pula bloqueantes).
 */
import { Fragment, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Group,
  MultiSelect,
  PasswordInput,
  SegmentedControl,
  Select,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconChevronDown, IconChevronRight, IconPlugConnected, IconRefresh } from "@tabler/icons-react";
import { api } from "../api";
import { fmtCompetencia, fmtDataHora, fmtMoeda } from "../format";
import { notificarOk, notificarAviso, notificarErroApi } from "../componentes/notificacoes";
import { Carregando, Vazio } from "../componentes/Estados";
import { TabelaDados, Th, TdNum } from "../componentes/listas";

interface Contagens {
  criados: number;
  atualizados: number;
  inalterados: number;
  resolvidas: number;
  erros: number;
  errosDetalhes: string[];
  alertas: string[];
  /** M5.3 — benefícios: auto-aplicadas / tipos criados / pendentes sem de-para */
  autoAplicadas?: number;
  tiposCriados?: number;
  pendentesSemMapa?: number;
}
interface DeparaEntrada {
  uuid: string;
  nomeConvenia: string;
  cpfConvenia: string | null;
  registration: string | null;
  acao: "ja_vinculada" | "vinculada_cpf" | "criada";
  funcionarioId: string;
  nomeInterno: string;
  alertas: string[];
}
interface SyncJson {
  id: string;
  escopo: { dominios: string[]; pessoas: string[] };
  status: string; // "em_andamento" | "concluido" | "concluido_com_erros" | "falha"
  inicioEm: string;
  fimEm: string | null;
  resumo: { porDominio: Record<string, Contagens>; depara: DeparaEntrada[] } | null;
  erro: string | null;
  // batch (modo job): progresso ao vivo durante o poll
  dominioAtual: string | null;
  itensProcessados: number;
  itensTotal: number | null;
}
/** Evento do log persistente do sync (GET /integracoes/convenia/syncs/:id/eventos). */
interface EventoSync {
  id: string;
  dominio: string;
  entidade: string | null;
  entidadeNome: string | null;
  acao: string;
  status: string; // "ok" | "erro"
  detalhe: string | null;
  criadoEm: string;
}
interface StatusIntegracao {
  configurado: boolean;
  origem: string | null;
  dominios: string[];
  ultimoSync: SyncJson | null;
  espelho: {
    departamentos: number;
    cargos: number;
    centrosCusto: number;
    pessoasVinculadas: number;
    documentos: number;
    enderecos: number;
    contasBancarias: number;
    contatosEmergencia: number;
    asos: number;
    anotacoes: number;
    customFields: number;
    customFieldValores: number;
    dependentesPessoas: number;
    propostasPendentes: number;
    periodosAquisitivos: number;
    feriasSolicitacoes: number;
    afastamentos: number;
  };
}
interface Pessoa {
  id: string;
  conveniaUuid: string;
  nome: string;
  tipoVinculo: string;
}
interface Proposta {
  id: string;
  syncId: string | null;
  dominio: string;
  tipo: string;
  funcionarioId: string | null;
  conveniaUuid: string | null;
  motivo: string | null;
  vigenciaProposta: number | null;
  payloadAntes: Record<string, unknown> | null;
  payloadDepois: Record<string, unknown> & { alertas?: string[]; alertaBloqueante?: boolean };
  status: string;
  criadoEm: string;
}

const ROTULO_TIPO_PROPOSTA: Record<string, string> = {
  matricula: "Matrícula Domínio",
  nova_vigencia_contrato: "Nova vigência salarial",
  alteracao_vigente_salarial: "Divergência do vigente",
  cargo_divergente: "Cargo divergente (rótulo)",
  incluir_dependente: "Incluir dependente",
  alterar_dependente: "Alterar dependente",
  remover_dependente: "Remover dependente",
  revisar_dependente: "Revisar dependente (ambíguo)",
  desligamento: "Desligamento (inativar cadastro)",
  incluir_beneficio: "Incluir benefício",
  alterar_beneficio: "Alterar valores do plano",
  encerrar_beneficio: "Encerrar benefício",
  beneficio_nao_mapeado: "Benefício não mapeado",
};

const ROTULO_DOMINIO: Record<string, string> = {
  estrutura: "Estrutura (departamentos, cargos, centros de custo)",
  documentos: "Documentos & núcleo da pessoa",
  enderecos: "Endereços",
  contatos: "Contatos (e-mail/telefone)",
  bancario: "Dados bancários & PIX",
  emergencia: "Contatos de emergência",
  aso: "ASO (exames ocupacionais)",
  anotacoes: "Anotações do RH",
  campos_customizados: "Campos customizados (adesões, equipamentos…)",
  matricula: "Matrícula (registration × matrícula Domínio)",
  salarial: "Salário & cargo (histórico salarial)",
  dependentes: "Dependentes (IR / salário-família)",
  ferias: "Férias (períodos aquisitivos & solicitações)",
  ferias_coletivas: "Férias coletivas (espelho & vínculo das solicitações)",
  afastamentos: "Afastamentos & faltas",
  desligados: "Desligados (espelho & proposta de desligamento)",
  documentos_arquivos: "Arquivos (inventário & recibos de férias)",
  beneficios: "Benefícios (VT/VR/plano/seguro — vínculos)",
};
const ROTULO_ACAO: Record<string, string> = {
  ja_vinculada: "já vinculada",
  vinculada_cpf: "vinculada por CPF",
  criada: "criada",
};

/** Chaves internas do payload que não entram no diff legível. */
const CHAVES_OCULTAS_DIFF = new Set(["chave", "alertas", "alertaBloqueante", "cargoAlterado", "conveniaUuid", "benefitId", "vinculoId", "beneficiarioDerivado", "beneficiarios"]);

/** Valor legível do diff: centavos → R$, AAAAMM → mm/aaaa, boolean → sim/não. */
function valorLegivel(chave: string, valor: unknown): string {
  if (valor === null || valor === undefined || valor === "") return "—";
  if (typeof valor === "boolean") return valor ? "sim" : "não";
  if (typeof valor === "number") {
    if (/centavos/i.test(chave)) return fmtMoeda(valor);
    if (/^(vigencia|vigenciaInicio|vigenciaFim|vigenciaProposta)/.test(chave) && valor > 190001) return fmtCompetencia(valor);
    return String(valor);
  }
  return String(valor);
}

/** Linhas do diff antes→depois (união das chaves visíveis dos dois lados). */
function linhasDiff(p: Proposta): { chave: string; antes: string; depois: string }[] {
  const antes = p.payloadAntes ?? {};
  const depois = p.payloadDepois ?? {};
  const chaves = [...new Set([...Object.keys(antes), ...Object.keys(depois)])].filter((k) => !CHAVES_OCULTAS_DIFF.has(k));
  return chaves.map((chave) => ({
    chave,
    antes: p.dominio === "dependentes" && p.tipo === "incluir_dependente" ? "—" : valorLegivel(chave, antes[chave]),
    depois: valorLegivel(chave, depois[chave]),
  }));
}

export function IntegracaoConveniaPage() {
  const [status, setStatus] = useState<StatusIntegracao | null>(null);
  const [syncs, setSyncs] = useState<SyncJson[] | null>(null);
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [propostas, setPropostas] = useState<Proposta[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [token, setToken] = useState("");
  const [salvandoToken, setSalvandoToken] = useState(false);
  const [rodandoSync, setRodandoSync] = useState(false);
  // batch (modo job): sync em andamento (disparo 202 → poll 2s → toast no fim)
  const [jobAtivo, setJobAtivo] = useState<{ id: string; rotulo: string } | null>(null);
  const [jobProgresso, setJobProgresso] = useState<SyncJson | null>(null);
  const [dominiosSel, setDominiosSel] = useState<string[]>([]);
  const [pessoasSel, setPessoasSel] = useState<string[]>([]);
  const [aberto, setAberto] = useState<string | null>(null);
  const [detalhe, setDetalhe] = useState<Record<string, SyncJson>>({});
  // E10c — fila de propostas
  const [filtroDominio, setFiltroDominio] = useState<string | null>(null);
  const [propostaAberta, setPropostaAberta] = useState<string | null>(null);
  const [vigenciaInputs, setVigenciaInputs] = useState<Record<string, string>>({});
  const [aplicandoLote, setAplicandoLote] = useState(false);
  // M5.3 — beneficio_nao_mapeado: gestor escolhe o tipo interno na fila
  // (o de-para fica gravado; as próximas syncs auto-aplicam)
  const [tiposBeneficio, setTiposBeneficio] = useState<{ id: string; nome: string }[]>([]);
  const [tipoEscolhido, setTipoEscolhido] = useState<Record<string, string | null>>({});

  async function carregar() {
    try {
      const [s, h, p, pr, tb] = await Promise.all([
        api.get<StatusIntegracao>("/integracoes/convenia/status"),
        api.get<{ syncs: SyncJson[] }>("/integracoes/convenia/syncs"),
        api.get<{ pessoas: Pessoa[] }>("/integracoes/convenia/pessoas"),
        api.get<{ propostas: Proposta[] }>("/integracoes/convenia/propostas?status=pendente"),
        api.get<{ tipos: { id: string; nome: string }[] }>("/beneficio-tipos"),
      ]);
      setStatus(s);
      setSyncs(h.syncs);
      setPessoas(p.pessoas);
      setPropostas(pr.propostas);
      setTiposBeneficio(tb.tipos);
    } catch (e) {
      setErro(e);
    }
  }
  useEffect(() => {
    void carregar();
  }, []);

  async function salvarToken() {
    try {
      setSalvandoToken(true);
      await api.put("/integracoes/convenia/config", { token: token.trim() });
      notificarOk("Token do Convenia cadastrado.");
      setToken("");
      await carregar();
    } catch (e) {
      notificarErroApi(e);
    } finally {
      setSalvandoToken(false);
    }
  }

  async function rodarSync(corpo: { dominios?: string[]; pessoas?: string[] }, rotulo: string) {
    try {
      setRodandoSync(true);
      // batch (modo job): a API responde 202 imediato com o syncId — o acompanhamento
      // é pelo poll (useEffect abaixo), com toast de conclusão; single-flight na API
      const r = await api.post<{ syncId: string; status: string }>("/integracoes/convenia/sync", corpo);
      setJobAtivo({ id: r.syncId, rotulo });
      setJobProgresso(null);
      await carregar(); // o sync já aparece no histórico como "em_andamento"
    } catch (e) {
      notificarErroApi(e);
    } finally {
      setRodandoSync(false);
    }
  }

  // batch (modo job): poll a cada 2s enquanto há sync em andamento — mostra o
  // progresso (domínio atual, itens X/Y) e, ao concluir, o toast com o resumo
  // ("concluído: X ok, Y erros — ver detalhe") + recarrega as listas
  useEffect(() => {
    if (!jobAtivo) return;
    let cancelado = false;
    const tick = async () => {
      try {
        const r = await api.get<{ sync: SyncJson }>(`/integracoes/convenia/syncs/${jobAtivo.id}`);
        if (cancelado) return;
        setJobProgresso(r.sync);
        if (r.sync.status === "em_andamento") return;
        setJobAtivo(null);
        if (r.sync.status === "concluido") {
          const contagens = Object.entries(r.sync.resumo?.porDominio ?? {})
            .map(([d, c]) => `${d}: +${c.criados} ~${c.atualizados} =${c.inalterados}${c.erros ? ` !${c.erros}` : ""}`)
            .join(" · ");
          notificarOk(`${jobAtivo.rotulo} concluído — ${contagens || "sem alterações"}.`);
        } else if (r.sync.status === "concluido_com_erros") {
          notificarAviso(
            `${jobAtivo.rotulo} concluído com erros (${r.sync.erro ?? "ver detalhe"}) — abra o sync no histórico para ver o log por item.`,
            "Concluído com erros",
          );
        } else {
          notificarErroApi(new Error(`${jobAtivo.rotulo} falhou: ${r.sync.erro ?? "erro desconhecido"}`));
        }
        await carregar();
      } catch {
        /* poll tolerante — tenta de novo no próximo tick */
      }
    };
    void tick();
    const intervalo = setInterval(() => void tick(), 2000);
    return () => {
      cancelado = true;
      clearInterval(intervalo);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobAtivo]);

  async function alternarDetalhe(id: string) {
    if (aberto === id) {
      setAberto(null);
      return;
    }
    setAberto(id);
    if (!detalhe[id]) {
      try {
        const r = await api.get<{ sync: SyncJson }>(`/integracoes/convenia/syncs/${id}`);
        setDetalhe((d) => ({ ...d, [id]: r.sync }));
      } catch (e) {
        notificarErroApi(e);
      }
    }
  }

  async function resolverProposta(p: Proposta, acao: "aplicar" | "rejeitar") {
    try {
      const vigTexto = (vigenciaInputs[p.id] ?? "").trim();
      const tipoId = tipoEscolhido[p.id] ?? undefined;
      const corpo =
        acao === "aplicar" && (vigTexto !== "" || tipoId)
          ? {
              ...(vigTexto !== "" ? { vigenciaInicio: Number(vigTexto) } : {}),
              ...(tipoId ? { beneficioTipoId: tipoId } : {}),
            }
          : undefined;
      await api.post(`/integracoes/convenia/propostas/${p.id}/${acao}`, corpo);
      notificarOk(acao === "aplicar" ? "Proposta aplicada." : "Proposta rejeitada.");
      await carregar();
    } catch (e) {
      notificarErroApi(e);
    }
  }

  /** Aplica em lote as propostas VISÍVEIS sem alerta bloqueante (o servidor
   *  também pula bloqueantes — o botão só antecipa a seleção). */
  async function aplicarLote(visiveis: Proposta[]) {
    const elegiveis = visiveis.filter((p) => p.payloadDepois?.alertaBloqueante !== true);
    if (elegiveis.length === 0) return;
    try {
      setAplicandoLote(true);
      const r = await api.post<{ resultados: { id: string; ok: boolean; erro?: string }[] }>(
        "/integracoes/convenia/propostas/aplicar-lote",
        { ids: elegiveis.map((p) => p.id) },
      );
      const falhas = r.resultados.filter((x) => !x.ok);
      if (falhas.length === 0) {
        notificarOk(`${r.resultados.length} proposta(s) aplicada(s) em lote.`);
      } else {
        notificarOk(
          `${r.resultados.length - falhas.length} aplicada(s); ${falhas.length} não aplicada(s): ` +
            falhas.map((f) => f.erro ?? "erro").join(" · "),
        );
      }
      await carregar();
    } catch (e) {
      notificarErroApi(e);
    } finally {
      setAplicandoLote(false);
    }
  }

  if (erro) {
    return (
      <>
        <Title order={2} mb="md">Integração Convenia</Title>
        <Alert color="red" title="Falha ao carregar">{String(erro)}</Alert>
      </>
    );
  }
  if (!status || !syncs || !propostas) return <Carregando linhas={6} />;

  return (
    <>
      <Group justify="space-between" mb={4}>
        <Title order={2}>Integração Convenia</Title>
        <Button
          leftSection={<IconRefresh size={16} />}
          onClick={() => void rodarSync({}, "Atualizar tudo")}
          loading={rodandoSync}
          disabled={!status.configurado || jobAtivo !== null}
          title={jobAtivo ? "Há um sync em andamento (single-flight) — aguarde a conclusão" : undefined}
        >
          Atualizar tudo
        </Button>
      </Group>
      {/* batch (modo job): progresso ao vivo do sync em andamento (poll 2s) */}
      {jobAtivo && (
        <Alert color="blue" variant="light" mb="md" icon={<IconRefresh size={16} />} title={`Sync em andamento — ${jobAtivo.rotulo}`}>
          <Text size="sm">
            {jobProgresso?.dominioAtual
              ? `Domínio: ${ROTULO_DOMINIO[jobProgresso.dominioAtual] ?? jobProgresso.dominioAtual} · itens ${jobProgresso.itensProcessados}/${jobProgresso.itensTotal ?? "?"}`
              : "Iniciando…"}
            {" — a janela pode ficar aberta; ao concluir, um aviso aparece aqui (você pode sair desta página e voltar depois)."}
          </Text>
        </Alert>
      )}
      <Text size="sm" c="dimmed" mb="md">
        Espelho local do Convenia — o sync traz a estrutura da empresa e os cadastros de pessoas (documentos,
        endereços, contatos, dados bancários, emergência, ASO, anotações e campos customizados). Mudanças calculáveis
        (matrícula, salário/cargo e dependentes de IR) chegam como PROPOSTAS na fila de staging (§4.5) e só viram
        vigência após confirmação aqui — dependentes de sócio, que não afetam cálculo, são aplicados direto.
      </Text>

      <Card withBorder shadow="xs" mb="md">
        <Group justify="space-between" mb="sm" wrap="wrap">
          <Title order={5}>Status</Title>
          <Badge color={status.configurado ? "sillis" : "red"} variant="light" leftSection={<IconPlugConnected size={12} />}>
            {status.configurado ? `token configurado (${status.origem})` : "token NÃO configurado"}
          </Badge>
        </Group>
        <Group gap="xl" wrap="wrap" mb="sm">
          <Text size="sm">Departamentos: <strong>{status.espelho.departamentos}</strong></Text>
          <Text size="sm">Cargos: <strong>{status.espelho.cargos}</strong></Text>
          <Text size="sm">Centros de custo: <strong>{status.espelho.centrosCusto}</strong></Text>
          <Text size="sm">Pessoas vinculadas: <strong>{status.espelho.pessoasVinculadas}</strong></Text>
          <Text size="sm">Documentos: <strong>{status.espelho.documentos}</strong></Text>
          <Text size="sm">Endereços: <strong>{status.espelho.enderecos}</strong></Text>
          <Text size="sm">Contas bancárias: <strong>{status.espelho.contasBancarias}</strong></Text>
          <Text size="sm">Emergência: <strong>{status.espelho.contatosEmergencia}</strong></Text>
          <Text size="sm">ASOs: <strong>{status.espelho.asos}</strong></Text>
          <Text size="sm">Anotações: <strong>{status.espelho.anotacoes}</strong></Text>
          <Text size="sm">Campos customizados: <strong>{status.espelho.customFieldValores}</strong> ({status.espelho.customFields} definições)</Text>
          <Text size="sm">Dependentes: <strong>{status.espelho.dependentesPessoas}</strong></Text>
          <Text size="sm">Períodos aquisitivos: <strong>{status.espelho.periodosAquisitivos}</strong></Text>
          <Text size="sm">Solicitações de férias: <strong>{status.espelho.feriasSolicitacoes}</strong></Text>
          <Text size="sm">Afastamentos: <strong>{status.espelho.afastamentos}</strong></Text>
        </Group>
        <Text size="xs" c="dimmed">
          {status.ultimoSync
            ? `Último sync: ${fmtDataHora(status.ultimoSync.inicioEm)} — ${status.ultimoSync.status}` +
              (status.ultimoSync.erro ? ` (${status.ultimoSync.erro})` : "")
            : "Nenhum sync executado ainda."}
        </Text>
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">Token da API</Title>
        <Group align="flex-end" wrap="wrap">
          <PasswordInput
            label={status.configurado ? "Substituir token (o atual nunca é exibido)" : "Token do Convenia"}
            placeholder="Cole o token da conta Convenia"
            value={token}
            onChange={(e) => setToken(e.currentTarget.value)}
            w={420}
          />
          <Button onClick={() => void salvarToken()} loading={salvandoToken} disabled={!token.trim()}>
            Salvar token
          </Button>
        </Group>
        <Text size="xs" c="dimmed" mt={4}>
          Somente leitura (GET) nesta fase. O valor fica no banco local e nunca sai pela API nem vai para a auditoria.
        </Text>
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">Sync parcial (avançado)</Title>
        <Group align="flex-end" wrap="wrap" gap="md">
          <Checkbox.Group
            label="Domínios"
            value={dominiosSel}
            onChange={setDominiosSel}
          >
            <Group gap="sm" mt={4}>
              {status.dominios.map((d) => (
                <Checkbox key={d} value={d} label={ROTULO_DOMINIO[d] ?? d} />
              ))}
            </Group>
          </Checkbox.Group>
          <MultiSelect
            label="Pessoas (opcional — vazio = todas as vinculadas)"
            data={pessoas.map((p) => ({
              value: p.conveniaUuid,
              label: `${p.nome}${p.tipoVinculo === "socio" ? " (sócio)" : ""}`,
            }))}
            value={pessoasSel}
            onChange={setPessoasSel}
            w={420}
            searchable
            clearable
          />
          <Button
            variant="light"
            onClick={() =>
              void rodarSync(
                {
                  ...(dominiosSel.length > 0 ? { dominios: dominiosSel } : {}),
                  ...(pessoasSel.length > 0 ? { pessoas: pessoasSel } : {}),
                },
                "Sync parcial",
              )
            }
            loading={rodandoSync}
            disabled={!status.configurado || jobAtivo !== null || (dominiosSel.length === 0 && pessoasSel.length === 0)}
          >
            Rodar sync parcial
          </Button>
        </Group>
        {pessoas.length === 0 && (
          <Text size="xs" c="dimmed" mt={4}>
            Nenhuma pessoa vinculada ainda — rode "Atualizar tudo" primeiro (o seletor lista só pessoas com UUID).
          </Text>
        )}
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">Histórico de syncs</Title>
        {syncs.length === 0 ? (
          <Vazio texto="Nenhum sync executado." acao="Atualizar tudo" onAcao={() => void rodarSync({}, "Atualizar tudo")} />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Quando</Th>
                <Th>Escopo</Th>
                <Th>Status</Th>
                <Th num>Resumo</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {syncs.map((s) => (
                <Fragment key={s.id}>
                  <Table.Tr
                    onClick={() => void alternarDetalhe(s.id)}
                    style={{ cursor: "pointer" }}
                    bg={aberto === s.id ? "gray.0" : undefined}
                  >
                    <Table.Td>
                      <Text size="sm">{fmtDataHora(s.inicioEm)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {status.dominios.length > 0 && s.escopo.dominios.length === status.dominios.length && s.escopo.pessoas.length === 0
                          ? "tudo"
                          : [...s.escopo.dominios, ...s.escopo.pessoas.map((p) => `pessoa ${p.slice(0, 8)}…`)].join(", ")}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={
                          s.status === "concluido" ? "sillis" : s.status === "concluido_com_erros" ? "yellow" : s.status === "falha" ? "red" : "blue"
                        }
                        variant="light"
                      >
                        {s.status}
                      </Badge>
                    </Table.Td>
                    <TdNum>
                      {s.status === "em_andamento" ? (
                        <Text size="xs" c="blue">
                          {s.dominioAtual ? `${ROTULO_DOMINIO[s.dominioAtual] ?? s.dominioAtual} · ${s.itensProcessados}/${s.itensTotal ?? "?"}` : "iniciando…"}
                        </Text>
                      ) : s.resumo ? (
                        (() => {
                          const cs = Object.values(s.resumo!.porDominio);
                          const criados = cs.reduce((x, c) => x + c.criados, 0);
                          const atualizados = cs.reduce((x, c) => x + c.atualizados, 0);
                          const erros = cs.reduce((x, c) => x + c.erros, 0);
                          return `${criados} criados · ${atualizados} atualizados${erros > 0 ? ` · ${erros} erros` : ""}`;
                        })()
                      ) : (
                        (s.erro ?? "—")
                      )}
                    </TdNum>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td colSpan={4} p={0}>
                      <Collapse expanded={aberto === s.id}>
                        <div style={{ padding: 12 }}>
                          {!detalhe[s.id] ? (
                            <Carregando linhas={2} />
                          ) : (
                            <>
                              {Object.entries(detalhe[s.id]!.resumo?.porDominio ?? {}).map(([d, c]) => (
                                <Text key={d} size="sm" mb={2}>
                                  <strong>{d}</strong>: {c.criados} criados · {c.atualizados} atualizados · {c.inalterados}{" "}
                                  inalterados
                                  {(c.resolvidas ?? 0) > 0 ? ` · ${c.resolvidas} resolvidas` : ""} · {c.erros} erros
                                  {(c.autoAplicadas ?? 0) > 0 ? (
                                    <Badge ml="xs" color="sillis" variant="light" size="sm">
                                      {c.autoAplicadas} auto-aplicadas
                                    </Badge>
                                  ) : null}
                                  {(c.tiposCriados ?? 0) > 0 ? (
                                    <Badge ml="xs" color="blue" variant="light" size="sm">
                                      {c.tiposCriados} tipos criados
                                    </Badge>
                                  ) : null}
                                  {(c.pendentesSemMapa ?? 0) > 0 ? (
                                    <Badge ml="xs" color="yellow" variant="light" size="sm">
                                      {c.pendentesSemMapa} pendentes sem de-para
                                    </Badge>
                                  ) : null}
                                  {(c.alertas ?? []).map((a, i) => (
                                    <Text key={`a${i}`} size="xs" c="yellow.8" ml="md">
                                      • {a}
                                    </Text>
                                  ))}
                                  {c.errosDetalhes.map((e, i) => (
                                    <Text key={i} size="xs" c="red.7" ml="md">
                                      • {e}
                                    </Text>
                                  ))}
                                </Text>
                              ))}
                              {/* batch — log persistente por item/domínio (filtro ok/erro) */}
                              <EventosSync syncId={s.id} />
                              {detalhe[s.id]!.resumo && detalhe[s.id]!.resumo!.depara.length > 0 && (
                                <TabelaDados mt="sm" maw={860}>
                                  <Table.Thead>
                                    <Table.Tr>
                                      <Th>Pessoa (Convenia)</Th>
                                      <Th>Registro</Th>
                                      <Th>Ação</Th>
                                      <Th>Cadastro interno</Th>
                                    </Table.Tr>
                                  </Table.Thead>
                                  <Table.Tbody>
                                    {detalhe[s.id]!.resumo!.depara.map((p) => (
                                      <Table.Tr key={p.uuid}>
                                        <Table.Td>
                                          <Text size="sm">{p.nomeConvenia}</Text>
                                          {p.alertas.map((a, i) => (
                                            <Text key={i} size="xs" c="yellow.8">
                                              • {a}
                                            </Text>
                                          ))}
                                        </Table.Td>
                                        <Table.Td>
                                          <Text ff="monospace" size="xs">{p.registration ?? "—"}</Text>
                                        </Table.Td>
                                        <Table.Td>
                                          <Badge
                                            variant="light"
                                            color={p.acao === "criada" ? "green" : p.acao === "vinculada_cpf" ? "sillis" : "gray"}
                                          >
                                            {ROTULO_ACAO[p.acao]}
                                          </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                          <Text size="sm">{p.nomeInterno}</Text>
                                        </Table.Td>
                                      </Table.Tr>
                                    ))}
                                  </Table.Tbody>
                                </TabelaDados>
                              )}
                            </>
                          )}
                        </div>
                      </Collapse>
                    </Table.Td>
                  </Table.Tr>
                </Fragment>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>

      <Card withBorder shadow="xs" mb="md">
        {(() => {
          const nomePessoa = (id: string | null) => pessoas.find((x) => x.id === id)?.nome ?? id ?? "—";
          const dominiosPresentes = [...new Set(propostas.map((p) => p.dominio))];
          const visiveis = filtroDominio ? propostas.filter((p) => p.dominio === filtroDominio) : propostas;
          // beneficio_nao_mapeado fica fora do lote: exige a escolha individual do tipo
          const elegiveisLote = visiveis.filter((p) => p.payloadDepois?.alertaBloqueante !== true && p.tipo !== "beneficio_nao_mapeado");
          return (
            <>
              <Group justify="space-between" mb="sm" wrap="wrap">
                <Title order={5}>
                  Propostas pendentes (staging calculável) <Badge variant="light">{visiveis.length}</Badge>
                </Title>
                <Group gap="sm">
                  {dominiosPresentes.length > 1 && (
                    <Select
                      size="xs"
                      placeholder="Domínio: todos"
                      clearable
                      data={dominiosPresentes.map((d) => ({ value: d, label: ROTULO_DOMINIO[d] ?? d }))}
                      value={filtroDominio}
                      onChange={setFiltroDominio}
                      w={280}
                    />
                  )}
                  <Button
                    size="xs"
                    variant="light"
                    disabled={elegiveisLote.length === 0}
                    loading={aplicandoLote}
                    onClick={() => void aplicarLote(visiveis)}
                  >
                    Aplicar em lote ({elegiveisLote.length})
                  </Button>
                </Group>
              </Group>
              {visiveis.length === 0 ? (
                <Vazio texto="Nenhuma proposta pendente — o staging das mudanças calculáveis (matrícula, salário/cargo, dependentes) está em dia." />
              ) : (
                <TabelaDados>
                  <Table.Thead>
                    <Table.Tr>
                      <Th></Th>
                      <Th>Pessoa</Th>
                      <Th>Domínio</Th>
                      <Th>Tipo</Th>
                      <Th>Vigência</Th>
                      <Th>Criada em</Th>
                      <Th>Ações</Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {visiveis.map((p) => {
                      const bloqueante = p.payloadDepois?.alertaBloqueante === true;
                      const alertas = p.payloadDepois?.alertas ?? [];
                      const expandida = propostaAberta === p.id;
                      return (
                        <Fragment key={p.id}>
                          <Table.Tr
                            onClick={() => setPropostaAberta(expandida ? null : p.id)}
                            style={{ cursor: "pointer" }}
                            bg={expandida ? "gray.0" : undefined}
                          >
                            <Table.Td w={28}>
                              {expandida ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{nomePessoa(p.funcionarioId)}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{p.dominio}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Group gap={6} wrap="nowrap">
                                <Text size="sm">{ROTULO_TIPO_PROPOSTA[p.tipo] ?? p.tipo}</Text>
                                {bloqueante && (
                                  <Badge color="red" variant="light" size="sm">
                                    decisão individual
                                  </Badge>
                                )}
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{p.vigenciaProposta ? fmtCompetencia(p.vigenciaProposta) : "—"}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs">{fmtDataHora(p.criadoEm)}</Text>
                            </Table.Td>
                            <Table.Td onClick={(e) => e.stopPropagation()}>
                              <Group gap="xs" wrap="nowrap">
                                {p.dominio === "salarial" && (
                                  <TextInput
                                    size="xs"
                                    w={92}
                                    placeholder="AAAAMM"
                                    title="Vigência de aplicação (obrigatória para divergência do vigente e para proposta retroativa a mês fechado)"
                                    value={vigenciaInputs[p.id] ?? ""}
                                    onChange={(e) =>
                                      setVigenciaInputs((v) => ({ ...v, [p.id]: e.currentTarget.value.replace(/\D/g, "").slice(0, 6) }))
                                    }
                                  />
                                )}
                                {p.tipo === "beneficio_nao_mapeado" && (
                                  <Select
                                    size="xs"
                                    w={230}
                                    placeholder="Tipo interno do benefício…"
                                    title="Escolha o tipo interno — o de-para fica gravado e as próximas syncs aplicam sozinhas"
                                    searchable
                                    data={tiposBeneficio.map((t) => ({ value: t.id, label: t.nome }))}
                                    value={tipoEscolhido[p.id] ?? null}
                                    onChange={(v) => setTipoEscolhido((m) => ({ ...m, [p.id]: v }))}
                                  />
                                )}
                                <Button
                                  size="xs"
                                  variant="light"
                                  disabled={p.tipo === "beneficio_nao_mapeado" && !tipoEscolhido[p.id]}
                                  title={
                                    p.tipo === "beneficio_nao_mapeado" && !tipoEscolhido[p.id]
                                      ? "Escolha o tipo interno antes de aplicar"
                                      : undefined
                                  }
                                  onClick={() => void resolverProposta(p, "aplicar")}
                                >
                                  Aplicar
                                </Button>
                                <Button size="xs" variant="subtle" color="red" onClick={() => void resolverProposta(p, "rejeitar")}>
                                  Rejeitar
                                </Button>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                          <Table.Tr>
                            <Table.Td colSpan={7} p={0}>
                              <Collapse expanded={expandida}>
                                <div style={{ padding: 12 }}>
                                  {p.motivo && (
                                    <Text size="sm" mb={4}>
                                      <strong>Motivo:</strong> {p.motivo}
                                    </Text>
                                  )}
                                  {alertas.length > 0 && (
                                    <Alert color={bloqueante ? "red" : "yellow"} variant="light" mb="sm" p="xs">
                                      {alertas.map((a, i) => (
                                        <Text key={i} size="xs">
                                          • {a}
                                        </Text>
                                      ))}
                                    </Alert>
                                  )}
                                  <TabelaDados maw={720}>
                                    <Table.Thead>
                                      <Table.Tr>
                                        <Th>Campo</Th>
                                        <Th>Antes (interno)</Th>
                                        <Th>Depois (proposta)</Th>
                                      </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                      {linhasDiff(p).map((l) => (
                                        <Table.Tr key={l.chave}>
                                          <Table.Td>
                                            <Text ff="monospace" size="xs">{l.chave}</Text>
                                          </Table.Td>
                                          <Table.Td>
                                            <Text size="sm" c={l.antes !== l.depois ? "red.7" : undefined}>{l.antes}</Text>
                                          </Table.Td>
                                          <Table.Td>
                                            <Text size="sm" c={l.antes !== l.depois ? "green.8" : undefined}>{l.depois}</Text>
                                          </Table.Td>
                                        </Table.Tr>
                                      ))}
                                    </Table.Tbody>
                                  </TabelaDados>
                                </div>
                              </Collapse>
                            </Table.Td>
                          </Table.Tr>
                        </Fragment>
                      );
                    })}
                  </Table.Tbody>
                </TabelaDados>
              )}
            </>
          );
        })()}
      </Card>
    </>
  );
}

/** Log persistente do sync (batch — pedido do dono: "log mostrando o que deu
 *  certo e o que deu errado"): eventos por item/domínio gravados em
 *  sync_eventos, com filtro erro/ok/todos (GET .../syncs/:id/eventos). */
function EventosSync({ syncId }: { syncId: string }) {
  const [filtro, setFiltro] = useState<string>("erro");
  const [eventos, setEventos] = useState<EventoSync[] | null>(null);
  useEffect(() => {
    let vivo = true;
    setEventos(null);
    api
      .get<{ eventos: EventoSync[] }>(`/integracoes/convenia/syncs/${syncId}/eventos${filtro ? `?status=${filtro}` : ""}`)
      .then((r) => {
        if (vivo) setEventos(r.eventos);
      })
      .catch(() => {
        if (vivo) setEventos([]);
      });
    return () => {
      vivo = false;
    };
  }, [syncId, filtro]);

  return (
    <div style={{ marginTop: 12 }}>
      <Group justify="space-between" mb={6}>
        <Title order={6}>Log de eventos</Title>
        <SegmentedControl
          size="xs"
          value={filtro}
          onChange={setFiltro}
          data={[
            { value: "erro", label: "Só erros" },
            { value: "ok", label: "Só sucessos" },
            { value: "", label: "Todos" },
          ]}
        />
      </Group>
      {eventos === null ? (
        <Carregando linhas={2} />
      ) : eventos.length === 0 ? (
        <Text size="xs" c="dimmed">
          Nenhum evento {filtro === "erro" ? "de erro" : filtro === "ok" ? "de sucesso" : ""} registrado neste sync
          (o log por item existe a partir do modo batch).
        </Text>
      ) : (
        <TabelaDados maw={860}>
          <Table.Thead>
            <Table.Tr>
              <Th>Quando</Th>
              <Th>Domínio</Th>
              <Th>Item</Th>
              <Th>Status</Th>
              <Th>Detalhe</Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {eventos.map((e) => (
              <Table.Tr key={e.id}>
                <Table.Td>
                  <Text size="xs">{fmtDataHora(e.criadoEm)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{e.dominio}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{e.entidadeNome ?? e.entidade ?? "—"}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light" color={e.status === "ok" ? "sillis" : "red"}>
                    {e.status === "ok" ? "ok" : "erro"}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c={e.status === "erro" ? "red.7" : "dimmed"}>
                    {e.detalhe ?? e.acao}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </TabelaDados>
      )}
    </div>
  );
}
